import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useContext, useMemo, } from 'react';
const TableContext = createContext(null);
function useTableContext(part) {
    const ctx = useContext(TableContext);
    if (ctx === null) {
        throw new Error(`<Table.${part}> must be rendered inside a <Table> component. ` +
            'Wrap your tree in <Table>...</Table> so the column template ' +
            'is shared with every descendant.');
    }
    return ctx;
}
// ── Table (root) ────────────────────────────────────────────────────────────
/**
 * Table — CSS-grid data table primitive.
 *
 * Renders a native `<table>` so screen readers + copy/paste + print
 * work without us having to replicate the semantics. Layout uses
 * CSS Grid (the SCSS overrides the default `display: table` chain),
 * with a single `grid-template-columns` propagated to every row via
 * a small internal context so header + body cells line up without
 * per-cell widths.
 *
 * For sortable columns use `<Table.SortableHead>` instead of
 * `<Table.Head>`. The sortable variant wires up the click handler,
 * the chevron indicator, and the `aria-sort` attribute.
 *
 * @example
 *   <Table columns="2fr 1fr 120px">
 *     <Table.Header>
 *       <Table.Row>
 *         <Table.Head>Name</Table.Head>
 *         <Table.Head>Email</Table.Head>
 *         <Table.Head>Role</Table.Head>
 *       </Table.Row>
 *     </Table.Header>
 *     <Table.Body>
 *       <Table.Row>
 *         <Table.Cell>Alice</Table.Cell>
 *         <Table.Cell>alice@example.com</Table.Cell>
 *         <Table.Cell>admin</Table.Cell>
 *       </Table.Row>
 *     </Table.Body>
 *   </Table>
 */
function TableBase({ columns, caption, className, children, ...rest }) {
    const ctx = useMemo(() => ({ columns }), [columns]);
    const classes = ['osd-table', className].filter(Boolean).join(' ');
    return (_jsx(TableContext.Provider, { value: ctx, children: _jsxs("table", { className: classes, style: gridTemplateColumns(columns), ...rest, children: [caption !== undefined && caption !== null ? (_jsx("caption", { className: "osd-table__caption", children: caption })) : null, children] }) }));
}
// ── Table.Header ────────────────────────────────────────────────────────────
function TableHeader({ className, children, ...rest }) {
    const { columns } = useTableContext('Header');
    const classes = ['osd-table__header', className].filter(Boolean).join(' ');
    return (_jsx("thead", { className: classes, style: gridTemplateColumns(columns), ...rest, children: children }));
}
// ── Table.Body ──────────────────────────────────────────────────────────────
function TableBody({ className, children, ...rest }) {
    const { columns } = useTableContext('Body');
    const classes = ['osd-table__body', className].filter(Boolean).join(' ');
    return (_jsx("tbody", { className: classes, style: gridTemplateColumns(columns), ...rest, children: children }));
}
// ── Table.Row ───────────────────────────────────────────────────────────────
function TableRow({ className, children, ...rest }) {
    const { columns } = useTableContext('Row');
    const classes = ['osd-table__row', className].filter(Boolean).join(' ');
    return (_jsx("tr", { className: classes, style: gridTemplateColumns(columns), ...rest, children: children }));
}
// ── Table.Head ──────────────────────────────────────────────────────────────
function TableHead({ className, children, ...rest }) {
    const classes = ['osd-table__head', className].filter(Boolean).join(' ');
    return (_jsx("th", { className: classes, scope: "col", ...rest, children: children }));
}
// ── Table.Cell ──────────────────────────────────────────────────────────────
function TableCell({ className, children, ...rest }) {
    const classes = ['osd-table__cell', className].filter(Boolean).join(' ');
    return (_jsx("td", { className: classes, ...rest, children: children }));
}
/**
 * Table.SortableHead — header cell with a sort affordance.
 *
 * Renders a `<button>` inside the `<th scope="col">` so the entire
 * label is the click target and keyboard activation comes for free
 * (Enter / Space). The `<th>` carries `aria-sort` so assistive tech
 * announces "ascending" / "descending" / "none" when the user
 * navigates to it. The chevron indicator (`▲` / `▼` / hidden) is
 * driven by the `direction` prop and styled by `.osd-table` SCSS.
 */
function TableSortableHead({ children, direction = 'none', onSort, className, ...rest }) {
    const indicator = sortIndicator(direction);
    const a11yLabel = sortAriaLabel(direction, children);
    const classes = ['osd-table__head', 'osd-table__head--sortable', className]
        .filter(Boolean)
        .join(' ');
    return (_jsx("th", { className: classes, scope: "col", "aria-sort": direction, "data-sort": direction, ...rest, children: _jsxs("button", { type: "button", className: "osd-table__sort-button", onClick: onSort, "aria-label": a11yLabel, children: [_jsx("span", { children: children }), indicator !== '' ? (_jsx("span", { className: "osd-table__sort-indicator", "aria-hidden": "true", children: indicator })) : null] }) }));
}
// ── Helpers ─────────────────────────────────────────────────────────────────
/**
 * The CSS Grid template for the table — set once on the root and
 * forwarded through context to every <thead> / <tbody> / <tr>.
 * `grid-template-columns` is NOT an inherited CSS property, so we
 * apply it explicitly on every grid container.
 */
function gridTemplateColumns(columns) {
    return { gridTemplateColumns: columns };
}
/** Visible chevron for a sortable head — empty when not actively sorted. */
function sortIndicator(direction) {
    if (direction === 'ascending')
        return '▲';
    if (direction === 'descending')
        return '▼';
    return '';
}
/** Human-readable label announced by screen readers when the head is focused. */
function sortAriaLabel(direction, children) {
    if (typeof children !== 'string')
        return undefined;
    if (direction === 'ascending')
        return `${children} — sorted ascending`;
    if (direction === 'descending')
        return `${children} — sorted descending`;
    return `Sort by ${children}`;
}
/** Public `<Table>` reference with the six compound-part slots typed. */
export const Table = Object.assign(TableBase, {
    Header: TableHeader,
    Body: TableBody,
    Row: TableRow,
    Head: TableHead,
    Cell: TableCell,
    SortableHead: TableSortableHead,
});
/**
 * Named exports of the same compound-part components reachable as
 * `<Table.Header>` / `<Table.Body>` / etc. Use these when you want
 * flat imports without the namespace object:
 *
 *   import { Table, TableHead } from '@osd/ui/table';
 *   <Table>
 *     <TableHead>Name</TableHead>
 *     ...
 *   </Table>
 */
export { TableHeader, TableBody, TableRow, TableHead, TableCell, TableSortableHead };
//# sourceMappingURL=Table.js.map